import { keepPreviousData, useQuery, type UseQueryOptions } from '@tanstack/vue-query';
import type { Ref } from 'vue';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { ChartMode, ChartPeriod } from '@/types';

const DEFAULT_OPTIONS = {
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
};

/** Backend monitor route per weight-tab chart mode. The Daily Gain view is served by the backend's "growth" route — a backend-internal name only. */
const WEIGHT_CHART_ENDPOINTS: Record<ChartMode, string> = {
    [ChartMode.GrowthIndex]: 'charts/monitor/growth-index',
    [ChartMode.DailyGain]: 'charts/monitor/growth',
    [ChartMode.Weight]: 'charts/monitor/weight-measurements',
};

export const queryKeys = {
    monitorOverviewCharts: () => ['charts', 'monitoring', 'monitorOverview'],
    useMonitorOverviewChartsEntry: ({ sectionId, hourly, period }: { sectionId: Ref<number>; hourly: Ref<boolean>; period: Ref<ChartPeriod> }) => [
        ...queryKeys.monitorOverviewCharts(),
        { sectionId, hourly, period },
    ],
    monitorWaterConsumptionCharts: () => ['charts', 'monitoring', 'monitorWaterConsumption'],
    monitorWaterConsumptionChart: ({ sectionId, hourly, period }: { sectionId: Ref<number>; hourly: Ref<boolean>; period: Ref<ChartPeriod> }) => [
        ...queryKeys.monitorWaterConsumptionCharts(),
        { sectionId, hourly, period },
    ],
    feedingSystemRuntimeCharts: () => ['charts', 'monitoring', 'feedingSystemRuntime'],
    feedingSystemRuntimeChart: ({ sectionId, hourly, period }: { sectionId: Ref<number>; hourly: Ref<boolean>; period: Ref<ChartPeriod> }) => [
        ...queryKeys.feedingSystemRuntimeCharts(),
        { sectionId, hourly, period },
    ],
    weightCharts: () => ['charts', 'monitoring', 'weight'],
    weightChart: ({ sectionId, hourly, period, mode }: { sectionId: Ref<number>; hourly: Ref<boolean>; period: Ref<ChartPeriod>; mode: Ref<ChartMode> }) => [
        ...queryKeys.weightCharts(),
        { sectionId, hourly, period, mode },
    ],
};

export function useMonitorOverviewCharts({ sectionId, hourly, period }: { sectionId: Ref<number>; hourly: Ref<boolean>; period: Ref<ChartPeriod> }, options: UseQueryOptions<any> = {}) {
    return useQuery<any>({
        queryKey: queryKeys.useMonitorOverviewChartsEntry({ sectionId, hourly, period }),
        queryFn: () =>
            store.dispatch(ActionType.GetMonitorOverviewCharts, {
                form: {
                    location_id: sectionId.value,
                    hourly: hourly.value,
                    period: period.value,
                },
            }),
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
        ...options,
    });
}

export function useMonitorWaterConsumptionChart({ sectionId, hourly, period }: { sectionId: Ref<number>; hourly: Ref<boolean>; period: Ref<ChartPeriod> }, options: UseQueryOptions<any> = {}) {
    return useQuery<any>({
        queryKey: queryKeys.monitorWaterConsumptionChart({ sectionId, hourly, period }),
        queryFn: () =>
            store.dispatch(ActionType.GetMonitorChartData, {
                form: {
                    url: 'charts/monitor/water-consumption',
                    location_id: sectionId.value,
                    hourly: hourly.value,
                    period: period.value,
                },
            }),
        ...DEFAULT_OPTIONS,
        ...options,
    });
}

export function useMonitorFeedingSystemRuntimeChart({ sectionId, hourly, period }: { sectionId: Ref<number>; hourly: Ref<boolean>; period: Ref<ChartPeriod> }, options: UseQueryOptions<any> = {}) {
    return useQuery<any>({
        queryKey: queryKeys.feedingSystemRuntimeChart({ sectionId, hourly, period }),
        queryFn: () =>
            store.dispatch(ActionType.GetMonitorChartData, {
                form: {
                    url: 'charts/monitor/feeding-sys-runtime',
                    location_id: sectionId.value,
                    hourly: hourly.value,
                    period: period.value,
                },
            }),
        ...DEFAULT_OPTIONS,
        ...options,
    });
}

export function useMonitorWeightChart({ sectionId, hourly, period, mode }: { sectionId: Ref<number>; hourly: Ref<boolean>; period: Ref<ChartPeriod>; mode: Ref<ChartMode> }, options: UseQueryOptions<any> = {}) {
    return useQuery<any>({
        queryKey: queryKeys.weightChart({ sectionId, hourly, period, mode }),
        queryFn: () =>
            store.dispatch(ActionType.GetMonitorChartData, {
                form: {
                    url: WEIGHT_CHART_ENDPOINTS[mode.value],
                    location_id: sectionId.value,
                    hourly: hourly.value,
                    period: period.value,
                },
            }),
        ...DEFAULT_OPTIONS,
        ...options,
    });
}
